import { z } from "zod";

// Sources
const UserSource = z.object({
  type: z.literal("user"),
  userId: z.string().optional(),
});
const GroupSource = z.object({
  type: z.literal("group"),
  groupId: z.string(),
  userId: z.string().optional(),
});
const RoomSource = z.object({
  type: z.literal("room"),
  roomId: z.string(),
  userId: z.string().optional(),
});
const Source = z.discriminatedUnion("type", [UserSource, GroupSource, RoomSource]);

// Messages
const TextMessage = z.object({
  id: z.string(),
  type: z.literal("text"),
  text: z.string(),
  quoteToken: z.string().optional(),
});
const ImageMessage = z.object({
  id: z.string(),
  type: z.literal("image"),
  contentProvider: z.object({ type: z.enum(["line", "external"]) }).optional(),
  quoteToken: z.string().optional(),
});
const OtherMessage = z.object({
  id: z.string(),
  type: z.enum(["sticker", "video", "audio", "location", "file"]),
  quoteToken: z.string().optional(),
});
const Message = z.union([TextMessage, ImageMessage, OtherMessage]);

// Events — v1 only handles "message"; other types parsed but ignored.
const MessageEvent = z.object({
  type: z.literal("message"),
  message: Message,
  timestamp: z.number(),
  source: Source,
  replyToken: z.string().optional(),
  mode: z.string().optional(),
  webhookEventId: z.string().optional(),
  deliveryContext: z.object({ isRedelivery: z.boolean() }).optional(),
});
const NonMessageEvent = z.object({
  type: z.enum(["follow", "unfollow", "join", "leave", "postback", "beacon"]),
  source: Source.optional(),
  timestamp: z.number(),
  webhookEventId: z.string().optional(),
  deliveryContext: z.object({ isRedelivery: z.boolean() }).optional(),
});
const LineEvent = z.union([MessageEvent, NonMessageEvent]);

export const LineWebhookBody = z.object({
  destination: z.string(),
  events: z.array(LineEvent),
});

export type LineWebhookBody = z.infer<typeof LineWebhookBody>;
export type LineEvent = z.infer<typeof LineEvent>;
export type LineMessageEvent = z.infer<typeof MessageEvent>;
export type LineTextMessageEvent = LineMessageEvent & { message: z.infer<typeof TextMessage> };
export type LineImageMessageEvent = LineMessageEvent & { message: z.infer<typeof ImageMessage> };

export function isMessageEvent(e: LineEvent): e is LineMessageEvent {
  return e.type === "message";
}

export function isTextMessageEvent(e: LineEvent): e is LineTextMessageEvent {
  return e.type === "message" && e.message.type === "text";
}

export function isImageMessageEvent(e: LineEvent): e is LineImageMessageEvent {
  return e.type === "message" && e.message.type === "image";
}

export function isUserSourceEvent(e: LineEvent): boolean {
  // True for ANY event (message or non-message) where source.type === "user".
  // Callers wanting message-narrowing should chain isMessageEvent(e) first.
  return "source" in e && e.source !== undefined && e.source.type === "user";
}
